import {
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  fetchWithRetry,
} from '@/lib/apiRequest';

export type GlobalSearchResult = {
  id:
    string;

  type:
    'client' |
    'lead' |
    'case' |
    'task' |
    'document' |
    'publication' |
    'communication';

  title:
    string;

  subtitle:
    string |
    null;

  meta:
    string |
    null;

  href:
    string;

  updatedAt:
    string |
    null;

  score:
    number;
};

export function useGlobalSearch(
  query:
    string,

  delay =
    250
) {
  const [
    results,
    setResults,
  ] =
    useState<
      GlobalSearchResult[]
    >([]);

  const [
    loading,
    setLoading,
  ] =
    useState(
      false
    );

  const [
    error,
    setError,
  ] =
    useState<
      string |
      null
    >(null);

  const requestId =
    useRef(
      0
    );

  useEffect(
    () => {
      const normalized =
        query.trim();

      if (
        normalized.length <
        2
      ) {
        setResults(
          []
        );

        setLoading(
          false
        );

        setError(
          null
        );

        return;
      }

      const current =
        ++requestId.current;

      const timer =
        window.setTimeout(
          async () => {
            setLoading(
              true
            );

            setError(
              null
            );

            try {
              const response =
                await fetchWithRetry(
                  `/api/global-search?q=${encodeURIComponent(
                    normalized
                  )}&limit=25`
                );

              const data =
                await response
                  .json() as {
                  success?:
                    boolean;

                  error?:
                    string;

                  results?:
                    GlobalSearchResult[];
                };

              if (
                !response.ok ||
                !data.success ||
                !Array.isArray(
                  data.results
                )
              ) {
                throw new Error(
                  data.error ||
                  'Falha na busca universal.'
                );
              }

              if (
                current ===
                requestId.current
              ) {
                setResults(
                  data.results
                );
              }
            } catch (
              searchError
            ) {
              if (
                current ===
                requestId.current
              ) {
                setError(
                  searchError instanceof Error
                    ? searchError.message
                    : 'Falha na busca universal.'
                );

                setResults(
                  []
                );
              }
            } finally {
              if (
                current ===
                requestId.current
              ) {
                setLoading(
                  false
                );
              }
            }
          },
          delay
        );

      return () => {
        window.clearTimeout(
          timer
        );
      };
    },
    [
      delay,
      query,
    ]
  );

  return {
    results,
    loading,
    error,
  };
}